import React, { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, PageBody } from "./_PageShell";
import { toast } from "sonner";

export default function Settings() {
  const { user, workspace, loadWorkspace } = useAuth();
  const [packs, setPacks] = useState([]);
  const [name, setName] = useState(workspace?.name || "");
  const [packSlug, setPackSlug] = useState(workspace?.pack_slug || "b2b-sales");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/crm/packs")
      .then(({ data }) => setPacks(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (workspace) {
      setName(workspace.name);
      setPackSlug(workspace.pack_slug);
    }
  }, [workspace]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/crm/workspace", {
        name: name.trim(),
        pack_slug: packSlug,
      });
      await loadWorkspace();
      toast.success("Workspace updated.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title={
          <>
            Workspace <span className="italic">settings.</span>
          </>
        }
        description="Update your workspace name and the industry pack that shapes labels and pipelines."
      />

      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <form
            data-testid="workspace-form"
            onSubmit={save}
            className="lg:col-span-2 p-7 rounded-2xl border border-[#E8E6E1] bg-[#FDFCFB] space-y-1"
          >
            <div>
              <label className="caption block mb-1">Workspace name</label>
              <input
                data-testid="workspace-name"
                className="editorial-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
                required
              />
            </div>

            <div className="mt-6">
              <label className="caption block mb-3">Industry pack</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {packs.map((p) => {
                  const active = p.slug === packSlug;
                  return (
                    <button
                      type="button"
                      key={p.slug}
                      data-testid={`pack-${p.slug}`}
                      onClick={() => setPackSlug(p.slug)}
                      className={`relative text-left p-3 rounded-xl border transition-all ${
                        active
                          ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FDFCFB]"
                          : "border-[#E8E6E1] bg-[#FDFCFB] text-[#1A1A1A] hover:border-[#1A1A1A]/40"
                      }`}
                    >
                      {active && (
                        <Check className="absolute top-2.5 right-2.5 w-3.5 h-3.5" />
                      )}
                      <div className="font-serif-display text-[15px] tracking-tight leading-tight pr-5">
                        {p.name}
                      </div>
                      <div
                        className={`mt-1 text-[11px] leading-snug ${
                          active ? "text-[#FDFCFB]/65" : "text-[#6B6A68]"
                        }`}
                      >
                        {p.tagline}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-[#6B6A68]">
                Changing the pack updates entity labels (e.g. "Contacts" →
                "Leads") immediately. Existing data is preserved.
              </p>
            </div>

            <div className="pt-6 flex items-center justify-end">
              <button
                type="submit"
                disabled={saving}
                data-testid="workspace-save"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333] disabled:opacity-60"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save changes
              </button>
            </div>
          </form>

          <div className="p-7 rounded-2xl bg-[#F5F4F0]">
            <div className="caption mb-4">Your account</div>
            <div className="font-serif-display text-xl text-[#1A1A1A]">
              {user?.name}
            </div>
            <div className="text-sm text-[#6B6A68] mt-1">{user?.email}</div>
            <div className="mt-6 caption">Role</div>
            <div className="text-sm text-[#1A1A1A] mt-1 capitalize">{user?.role}</div>

            <div className="mt-8 pt-6 border-t border-[#1A1A1A]/10">
              <p className="text-xs text-[#6B6A68] leading-relaxed">
                Team invites, password reset and profile editing land in the
                next chunk.
              </p>
            </div>
          </div>
        </div>
      </PageBody>
    </>
  );
}
